import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps, useMemo, useState, CSSProperties } from 'react';
import { HtmlVector } from 'record-it-utils/misc/HtmlVector/HtmlVector';
import { useElementSize } from '../../hooks/useElementSize/useElementSize';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import styles from './ClockPicker.module.css';
import { TickConfiguration, useCalculatedTickConfiguration } from './useCalculatedTickConfiguration';
import { useEventHandlers } from './useEventHandlers';
export type ClockPickerProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  value: number;
  tickConfiguration: TickConfiguration;
  /**
   * gets called only if a new value got selected
   */
  onValueChanged?: (value: number) => void;
  /**
   * gets called everytime a value got selected
   */
  onValueSelected?: (value: number) => void;
};
export const ClockPicker: FunctionComponent<ClockPickerProps> = React.memo(props => {
  const $ = _c(33);
  const {
    tickConfiguration,
    value,
    onValueChanged,
    onValueSelected,
    className,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const [container, setContainer] = useState(null);
  const containerSize = useElementSize(container);
  const calculatedTicks = useCalculatedTickConfiguration({
    containerSize,
    tickConfiguration
  });
  const {
    highlightedTick,
    containerProps
  } = useEventHandlers({
    value,
    onValueChanged,
    onValueSelected,
    container,
    allTicks: calculatedTicks.allTicks
  });
  let t0;
  bb0: {
    if (!containerSize || !highlightedTick) {
      t0 = null;
      break bb0;
    }
    let t1;
    let t2;
    if ($[0] !== containerSize.clientWidth || $[1] !== highlightedTick.position) {
      const vector = highlightedTick.position.substractVector(new HtmlVector(containerSize.clientWidth / 2, containerSize.clientWidth / 2));
      t2 = `${vector.getLength()}px`;
      t1 = vector.getAngle() || 0;
      $[0] = containerSize.clientWidth;
      $[1] = highlightedTick.position;
      $[2] = t1;
      $[3] = t2;
    } else {
      t1 = $[2];
      t2 = $[3];
    }
    const t3 = `rotate(${t1 * -1}deg) translate(0, -50%)`;
    let t4;
    if ($[4] !== t2 || $[5] !== t3) {
      t4 = {
        width: t2,
        transform: t3
      };
      $[4] = t2;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t0 = t4;
  }
  const highlightedTickCircleConnectorStyle = t0;
  let t1;
  if ($[7] !== highlightedTick) {
    t1 = (tick, testId, extraClass) => <div key={tick.displayedValue} className={classes(styles.tick, extraClass, tick === highlightedTick ? styles.tickHighlighted : null)} style={{
      left: `${tick.position.getX()}px`,
      top: `${tick.position.getY()}px`
    }} data-testid={testId}>{tick.displayedValue}</div>;
    $[7] = highlightedTick;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const renderTick = t1;
  const t2 = classes(className, styles.container);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={styles.centerPoint} />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== highlightedTickCircleConnectorStyle) {
    t4 = highlightedTickCircleConnectorStyle ? <div className={styles.highlightedTickCircleConnector} style={highlightedTickCircleConnectorStyle} /> : null;
    $[10] = highlightedTickCircleConnectorStyle;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== highlightedTick) {
    t5 = highlightedTick ? <div className={styles.highlightedTickCircle} style={{
      left: `${highlightedTick.position.getX()}px`,
      top: `${highlightedTick.position.getY()}px`
    }} data-testid="ClockPicker.HighlightCircle" /> : null;
    $[12] = highlightedTick;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== calculatedTicks.outerTicks || $[15] !== renderTick) {
    let t7;
    if ($[17] !== renderTick) {
      t7 = tick_0 => renderTick(tick_0, "ClockPicker.OuterTick", styles.outerTick);
      $[17] = renderTick;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t6 = calculatedTicks.outerTicks.filter(_temp).map(t7);
    $[14] = calculatedTicks.outerTicks;
    $[15] = renderTick;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[19] !== calculatedTicks.innerTicks || $[20] !== renderTick) {
    let t8;
    if ($[22] !== renderTick) {
      t8 = tick_1 => renderTick(tick_1, "ClockPicker.InnerTick", styles.innerTick);
      $[22] = renderTick;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t7 = calculatedTicks.innerTicks.filter(_temp2).map(t8);
    $[19] = calculatedTicks.innerTicks;
    $[20] = renderTick;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[24] !== containerProps || $[25] !== remainingProps || $[26] !== setContainer || $[27] !== t2 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t8 = <div {...remainingProps} {...containerProps} className={t2} ref={setContainer}>{t3}{t4}{t5}{t6}{t7}</div>;
    $[24] = containerProps;
    $[25] = remainingProps;
    $[26] = setContainer;
    $[27] = t2;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
});
type CalculatedTick = {
  value: number;
  displayedValue: string;
  position: HtmlVector;
};
function _temp(t) {
  return t.selectable;
}
function _temp2(t_0) {
  return t_0.selectable;
}